<template>
  <v-dialog
    :model-value="modelValue"
    max-width="420"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <v-card>
      <div class="outline-dialog__header text-white">
        {{ slotId ? t('marketing-agent.outline.edit') : t('marketing-agent.outline.add') }}
      </div>
      <v-card-text class="pt-4">
        <p
          v-if="generated"
          class="outline-dialog__hint"
        >
          {{ t('marketing-agent.outline.reschedule-hint') }}
        </p>
        <v-select
          v-model="draft.platform"
          :items="platformItems"
          :loading="platformsLoading"
          :label="t('marketing-agent.outline.platform')"
          variant="outlined"
          density="comfortable"
          hide-details
          class="mb-4"
        />
        <RetryNotice
          v-if="connectionsError"
          class="mb-4"
          :message="t('marketing-agent.outline.connections-error')"
          :state="connectionsRetry.state.value"
          :loading="platformsLoading"
          @retry="onRetryConnections"
        />
        <p
          v-else-if="!platformsLoading && !platformItems.length"
          class="outline-dialog__hint"
        >
          {{ t('marketing-agent.outline.no-platforms') }}
        </p>
        <v-menu
          v-model="showDatePicker"
          :close-on-content-click="false"
          min-width="auto"
        >
          <template #activator="{ props: menuProps }">
            <v-text-field
              :model-value="formattedDate"
              :label="t('marketing-agent.wizard.date-label')"
              variant="outlined"
              density="comfortable"
              readonly
              hide-details
              class="mb-4"
              v-bind="menuProps"
            />
          </template>
          <v-date-picker
            :min="minDate"
            @update:model-value="onDatePicked"
          />
        </v-menu>
        <v-menu
          v-model="showTimePicker"
          :close-on-content-click="false"
          max-width="290"
          min-width="290"
        >
          <template #activator="{ props: menuProps }">
            <v-text-field
              :model-value="formattedTime"
              :label="t('marketing-agent.outline.time')"
              prepend-inner-icon="mdi-clock-time-four-outline"
              variant="outlined"
              density="comfortable"
              readonly
              hide-details
              v-bind="menuProps"
            />
          </template>
          <v-time-picker
            v-if="showTimePicker"
            v-model="draft.time"
            :format="timePickerFormat()"
          />
        </v-menu>
      </v-card-text>
      <v-card-actions>
        <v-btn
          v-if="slotId"
          color="error"
          variant="text"
          :disabled="saving"
          @click="emit('remove', slotId)"
        >
          {{ t('marketing-agent.outline.remove') }}
        </v-btn>
        <v-spacer />
        <v-btn
          variant="text"
          @click="emit('update:modelValue', false)"
        >
          {{ t('marketing-agent.outline.cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          :disabled="!canSave"
          :loading="saving"
          @click="save"
        >
          {{ t('marketing-agent.outline.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useSocialMedia } from '@/composables/useSocialMedia'
import { MarketingPlanSlotTextStatus, SocialMediaType } from '@/generated/graphql'
import { DATE_LONG, formatDate, getTime, timePickerFormat } from '@/lib/dateTime'

import { useRetryLimit } from './composables/useRetryLimit'
import { reportError } from './lib/reportError'
import RetryNotice from './RetryNotice.vue'

const READABLE = {
  FACEBOOK: 'Facebook',
  INSTAGRAM: 'Instagram',
  LINKEDIN: 'LinkedIn',
  GOOGLE_BUSINESS: 'Google Business',
}

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  slotData: { type: Object, default: null },
  saving: { type: Boolean, default: false },
  knownPlatforms: { type: Array, default: () => [] },
})

const emit = defineEmits(['update:modelValue', 'save', 'remove'])

const { t } = useI18n()
const {
  socialMediaConnections,
  isGoogleBusinessConnected,
  canUseGoogleBusiness,
  loadSocialMediaConnections,
  refetchSocialMediaConnections,
  loadGoogleBusinessStatus,
} = useSocialMedia()
const platformsLoading = ref(false)
const connectionsError = ref(false)
const connectionsRetry = useRetryLimit('outline-load-connections')

const showDatePicker = ref(false)
const showTimePicker = ref(false)
const draft = ref({ platform: '', date: '', time: '09:00' })

const slotId = computed(() => props.slotData?.id ?? null)

// Moving a post whose text is already written: the text may mention the old day.
const generated = computed(
  () =>
    !!slotId.value &&
    !!props.slotData?.textStatus &&
    props.slotData.textStatus !== MarketingPlanSlotTextStatus.Draft
)

const platformItems = computed(() => {
  const connected = new Set([...(socialMediaConnections.value || []), ...props.knownPlatforms])
  if (canUseGoogleBusiness.value && isGoogleBusinessConnected.value) {
    connected.add(SocialMediaType.GoogleBusiness)
  }
  return [
    SocialMediaType.Facebook,
    SocialMediaType.Instagram,
    SocialMediaType.Linkedin,
    SocialMediaType.GoogleBusiness,
  ]
    .filter((platform) => connected.has(platform))
    .map((platform) => ({ title: READABLE[platform], value: platform }))
})

const minDate = computed(() => {
  const tomorrow = new Date()
  tomorrow.setHours(0, 0, 0, 0)
  tomorrow.setDate(tomorrow.getDate() + 1)
  return tomorrow
})

// draft keeps 'YYYY-MM-DD' / 'HH:mm'; parse the time without 'Z' so it stays local
const formattedDate = computed(() => formatDate(draft.value.date, DATE_LONG))
const formattedTime = computed(() =>
  draft.value.time ? getTime(`1970-01-01T${draft.value.time}`) : ''
)

const canSave = computed(() => draft.value.platform && draft.value.date && draft.value.time)

watch(
  () => [props.modelValue, props.slotData],
  async () => {
    if (!props.modelValue) return
    const slot = props.slotData
    draft.value = {
      platform: slot?.platform || '',
      date: slot?.scheduledLocal?.date || '',
      time: slot?.scheduledLocal?.time || '09:00',
    }
    platformsLoading.value = true
    try {
      await loadSocialMediaConnections()
      connectionsRetry.reset()
      connectionsError.value = false
      if (canUseGoogleBusiness.value) loadGoogleBusinessStatus()
    } catch (error) {
      // The list falls back to the plan's own platforms (knownPlatforms).
      reportError('outline-load-connections', error, { slotId: slotId.value })
      connectionsRetry.noteFailure()
      connectionsError.value = true
    } finally {
      platformsLoading.value = false
    }
    selectFirstPlatform()
  }
)

function selectFirstPlatform() {
  if (!draft.value.platform) {
    draft.value = { ...draft.value, platform: platformItems.value[0]?.value || '' }
  }
}

async function onRetryConnections() {
  platformsLoading.value = true
  try {
    await connectionsRetry.run(() => refetchSocialMediaConnections(), {
      operation: 'reload-outline-connections',
      context: { slotId: slotId.value },
    })
    connectionsError.value = false
    selectFirstPlatform()
  } catch {
    // RetryNotice shows the next attempt, or the support hint.
  } finally {
    platformsLoading.value = false
  }
}

function onDatePicked(value) {
  if (!value) return
  const picked = new Date(value)
  const month = String(picked.getMonth() + 1).padStart(2, '0')
  const day = String(picked.getDate()).padStart(2, '0')
  draft.value.date = `${picked.getFullYear()}-${month}-${day}`
  showDatePicker.value = false
}

function save() {
  if (!canSave.value) return
  emit('save', {
    slotId: slotId.value,
    platform: draft.value.platform,
    date: draft.value.date,
    time: draft.value.time,
  })
}
</script>

<style scoped>
.outline-dialog__hint {
  margin: 0 0 16px;
  color: #5f6368;
  font-size: 14px;
  line-height: 1.45;
}

.outline-dialog__header {
  padding: 14px 18px;
  background: rgb(var(--v-theme-primary));
  font-size: 18px;
  font-weight: 600;
}
</style>
